import React from "react";
import { Link, useParams } from "react-router-dom";
import { getAuthorBySlug } from "../data/authors";
import { getBooksByAuthor } from "../data/books";
import { BookCard } from "../components/BookCard";
import { SEO } from "../components/SEO";

export default function AuthorProfile() {
  const { slug } = useParams();
  const author = getAuthorBySlug(slug);
  const authorBooks = author ? getBooksByAuthor(author.slug) : [];

  if (!author) {
    return (
      <div className="py-40 text-center">
        <p className="font-editorial italic text-3xl text-ink-muted">Author not found.</p>
        <Link to="/books" className="mt-8 inline-block text-crimson link-underline uppercase tracking-[0.24em] text-sm">
          ← Back to catalogue
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="author-profile-page">
      <SEO
        title={`${author.name} — ${author.tier}`}
        path={`/authors/${author.slug}`}
        description={author.shortBio}
        keywords={`${author.name}, CBH author, ${author.tier}`}
        type="profile"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: author.name,
          description: author.shortBio,
          jobTitle: author.tier,
          url: `https://conlangbookhouse.com/authors/${author.slug}`,
          worksFor: { "@type": "Organization", name: "Conlang Book House" },
        }}
      />
      {/* Hero */}
      <section className="pt-16 lg:pt-24 pb-16 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div
              className="aspect-square flex items-center justify-center font-editorial text-9xl text-ivory"
              style={{ backgroundColor: author.accent }}
              data-testid="author-photo"
            >
              {author.photoInitials}
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ {author.tier}</p>
            <h1 className="font-editorial text-6xl md:text-8xl leading-[0.9] tracking-tight text-ink" data-testid="author-name">
              {author.name}
            </h1>
            <p className="editorial-italic text-2xl mt-4 text-ink-muted">{author.tagline}</p>
            <div className="mt-10 max-w-2xl text-lg leading-relaxed text-ink-soft whitespace-pre-line dropcap">
              {author.fullBio}
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-16 bg-ivory-soft">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Credentials</p>
            <h3 className="font-editorial text-3xl text-ink mb-6">Selected recognitions.</h3>
            <ul className="space-y-4">
              {author.credentials.map((c, i) => (
                <li key={i} className="flex items-baseline gap-4 border-b border-crimson/15 pb-4">
                  <span className="editorial-italic text-crimson">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-ink-soft">{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-12 md:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Available for</p>
            <h3 className="font-editorial text-3xl text-ink mb-6">Speaking & engagements.</h3>
            <ul className="space-y-3">
              {author.availableFor.map((a, i) => (
                <li key={i} className="text-ink-soft flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-gold" /> {a}
                </li>
              ))}
            </ul>
            <Link
              to="/contact?inquiry_type=media"
              className="mt-8 inline-flex items-center gap-3 border border-crimson/40 text-crimson px-6 py-3 text-xs uppercase tracking-[0.24em] hover:bg-crimson hover:text-ivory transition-colors duration-500"
              data-testid="author-booking-cta"
            >
              Book this Author →
            </Link>
          </div>
        </div>
      </section>

      {/* Backlist */}
      <section className="py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ The Backlist</p>
          <h2 className="font-editorial text-4xl md:text-6xl text-ink leading-tight mb-14">
            Books by {author.name.split(" ")[0]}.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-12">
            {authorBooks.map((b, i) => (
              <BookCard key={b.slug} book={b} index={i} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
